import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { EditModeProvider } from "@/context/EditModeContext";
import { EditToolbar } from "@/components/site/edit/EditToolbar";
import { useLenis, scrollToSelector, resolveHashTarget } from "@/hooks/useLenis";
import { SiteAnalytics } from "@/components/site/SiteAnalytics";
import Home from "@/pages/Home";
import About from "@/pages/About";
import StudentCardSignup from "@/pages/StudentCardSignup";
import StudentCardPage from "@/pages/StudentCardPage";
import EventsPage from "@/pages/EventsPage";
import EventDetail from "@/pages/EventDetail";
import ProgramDetail from "@/pages/ProgramDetail";
import HolidayPage from "@/pages/HolidayPage";
import ClassesPage from "@/pages/ClassesPage";
import KosherPage from "@/pages/KosherPage";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import DonateResult from "@/pages/DonateResult";
import ServicesPage from "@/pages/ServicesPage";
import KosherEstablishmentsPage from "@/pages/KosherEstablishmentsPage";
import TouristDeskPage from "@/pages/TouristDeskPage";

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    let cancelled = false;
    let count = 0;
    const tick = () => {
      if (cancelled) return;
      count += 1;
      const el = resolveHashTarget(hash);
      if (el) {
        const top = el.getBoundingClientRect().top;
        // Already settled ~90px below the fixed navbar → stop.
        if (top > 55 && top < 125) return;
        scrollToSelector(hash, -90);
      }
      if (count < 25) setTimeout(tick, 150);
    };
    const t = setTimeout(tick, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pathname, hash]);
  return null;
}

// Preserve scroll position across history (restore on Back/Forward, reset to top on new pages).
// Backed by sessionStorage so it survives mobile browsers discarding/reloading the previous
// page when you navigate away and come back (common on Android Chrome). React Router persists
// location.key in history.state, so the key stays stable even across a reload.
const SP_PREFIX = "sp:";
const savePos = (key, y) => { try { sessionStorage.setItem(SP_PREFIX + key, String(Math.round(y))); } catch (e) {} };
const readPos = (key) => { try { const v = sessionStorage.getItem(SP_PREFIX + key); return v == null ? null : parseInt(v, 10); } catch (e) { return null; } };

function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  // Continuously remember where the user is on the current history entry.
  useEffect(() => {
    const key = location.key;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => savePos(key, window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Also persist right before the tab is hidden/unloaded (mobile back can discard the page).
    const onHide = () => savePos(key, window.scrollY);
    window.addEventListener("pagehide", onHide);
    window.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("visibilitychange", onHide);
      cancelAnimationFrame(raf);
      // Final synchronous save before this history entry is left.
      savePos(key, window.scrollY);
    };
  }, [location.key]);

  // Restore on Back/Forward; scroll to top on new navigations.
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (location.hash) return; // hash targets are handled by ScrollToHash
    const goTo = (y) => {
      if (window.__lenis) window.__lenis.scrollTo(y, { immediate: true });
      else window.scrollTo(0, y);
    };
    // A fresh page load AND a reload both report navType "POP" (same as a real
    // Back/Forward). Only treat it as a genuine Back/Forward when it is NOT the
    // initial mount — otherwise the page snaps back to a stored scroll position
    // on first load / refresh and appears to "fight" the user's scroll.
    const wasFirstRun = firstRunRef.current;
    firstRunRef.current = false;
    // The homepage always opens at the top — never run the scroll-restore rAF loop
    // there, since on this tall, image-heavy page it can fight the user's first
    // scrolls (snap-back glitch). Back/forward restore still applies to other pages.
    if (navType === "POP" && !wasFirstRun && location.pathname !== "/") {
      const saved = readPos(location.key);
      if (saved != null && saved > 0) {
        let cancelled = false;
        const start = Date.now();
        // Stop fighting the user the moment they scroll themselves. Only a real
        // scroll drag (touchmove/wheel) counts — NOT touchstart, since a tap or the
        // tail of an Android edge back-swipe would otherwise cancel restoration.
        const abort = () => { cancelled = true; teardown(); };
        const teardown = () => {
          clearTimeout(armTimer);
          window.removeEventListener("wheel", abort);
          window.removeEventListener("touchmove", abort);
          window.removeEventListener("keydown", abort);
        };
        // Arm the abort listeners slightly late so the leftover touchmove events
        // from a back-swipe gesture don't immediately cancel the restore.
        const armTimer = setTimeout(() => {
          window.addEventListener("wheel", abort, { passive: true });
          window.addEventListener("touchmove", abort, { passive: true });
          window.addEventListener("keydown", abort);
        }, 400);
        const restore = () => {
          if (cancelled) return;
          const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          goTo(Math.min(saved, max));
          // Keep correcting until the page is tall enough to reach the saved spot
          // (images/reveals may still be growing), or we time out.
          if (window.scrollY < saved - 2 && Date.now() - start < 3000) {
            requestAnimationFrame(restore);
          } else {
            teardown();
          }
        };
        requestAnimationFrame(restore);
        return () => { cancelled = true; teardown(); };
      }
    }
    goTo(0);
  }, [location.key, location.hash, navType]);

  return null;
}

function App() {
  useLenis();
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <SiteAnalytics />
          <ScrollManager />
          <ScrollToHash />
          <EditModeProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/programs/:slug" element={<ProgramDetail />} />
            <Route path="/holidays" element={<HolidayPage />} />
            <Route path="/holidays/:id" element={<HolidayPage />} />
            <Route path="/students/card" element={<StudentCardSignup />} />
            <Route path="/card/:code" element={<StudentCardPage />} />
            <Route path="/classes" element={<ClassesPage />} />
            <Route path="/kosher" element={<KosherPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/donate/success" element={<DonateResult />} />
            <Route path="/donate/cancel" element={<DonateResult />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/kosher-establishments" element={<KosherEstablishmentsPage />} />
            <Route path="/tourist-desk" element={<TouristDeskPage />} />
          </Routes>
          <EditToolbar />
          </EditModeProvider>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
